import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import PasswordChanger from '../components/PasswordChanger';
import CarEditor from '../components/CarEditor';
import RentalAgreements from '../pages/AdminAgreementsPage';

export default function AdminPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalCars: 0,
    availableCars: 0,
    rentedCars: 0,
    totalRentals: 0,
    activeRentals: 0,
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [newOwnerEmail, setNewOwnerEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchOwnerEmail();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch cars stats
      const carsSnapshot = await getDocs(collection(db, 'cars'));
      const cars = carsSnapshot.docs.map(doc => doc.data());
      
      // Fetch rentals stats
      const rentalsSnapshot = await getDocs(collection(db, 'rentals'));
      const rentals = rentalsSnapshot.docs.map(doc => doc.data());

      setStats({
        totalCars: cars.length,
        availableCars: cars.filter(car => car.status === 'available').length,
        rentedCars: cars.filter(car => car.status === 'rented').length,
        totalRentals: rentals.length,
        activeRentals: rentals.filter(rental => rental.status === 'active').length,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOwnerEmail = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'owner'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setOwnerEmail(data.email || '');
        setNewOwnerEmail(data.email || '');
      }
    } catch (error) {
      console.error('Error fetching owner email:', error);
    }
  };

  const handleSaveOwnerEmail = async () => {
    if (!newOwnerEmail.trim()) {
      alert('Please enter a valid email address.');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newOwnerEmail)) {
      alert('Please enter a valid email address.');
      return;
    }

    setSavingEmail(true);
    try {
      await setDoc(doc(db, 'settings', 'owner'), {
        email: newOwnerEmail.trim(),
        updatedAt: new Date(),
      });
      
      setOwnerEmail(newOwnerEmail.trim());
      alert('Owner email updated successfully!');
    } catch (error) {
      console.error('Error saving owner email:', error);
      alert('Failed to update owner email. Please try again.');
    } finally {
      setSavingEmail(false);
    }
  };

  const tabs = [
    { id: 'overview', label: '📊 Overview', icon: '📊' },
    { id: 'cars', label: '🚗 Manage Cars', icon: '🚗' },
    { id: 'agreements', label: '📋 Agreements', icon: '📋' },
    { id: 'settings', label: '⚙️ Settings', icon: '⚙️' },
    { id: 'security', label: '🔒 Security', icon: '🔒' },
  ];

  if (loading) {
    return (
      <div style={loadingContainer}>
        <div style={spinner}></div>
        <p>Loading admin dashboard...</p>
      </div>
    );
  }

  return (
    <div style={pageWrapper}>
      {/* Header */}
      <div style={header}>
        <div style={headerLeft}>
          <button onClick={() => navigate('/')} style={backButton}>
            ← Back to Dashboard
          </button>
          <h1 style={title}>🛠️ Admin Panel</h1>
        </div>
        <div style={headerRight}>
          <span style={userInfo}>Welcome, Admin</span>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={tabContainer}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...tabButton,
              ...(activeTab === tab.id ? activeTabButton : {})
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div style={contentArea}>
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div style={overviewContainer}>
            <h2 style={sectionTitle}>Dashboard Overview</h2>
            
            {/* Stats Cards */}
            <div style={statsGrid}>
              <div style={statCard}>
                <div style={statIcon}>🚗</div>
                <div style={statContent}>
                  <h3 style={statNumber}>{stats.totalCars}</h3>
                  <p style={statLabel}>Total Cars</p>
                </div>
              </div>
              
              <div style={statCard}>
                <div style={statIcon}>✅</div>
                <div style={statContent}>
                  <h3 style={statNumber}>{stats.availableCars}</h3>
                  <p style={statLabel}>Available Cars</p>
                </div>
              </div>
              
              <div style={statCard}>
                <div style={statIcon}>🔒</div>
                <div style={statContent}>
                  <h3 style={statNumber}>{stats.rentedCars}</h3>
                  <p style={statLabel}>Rented Cars</p>
                </div>
              </div>
              
              <div style={statCard}>
                <div style={statIcon}>📋</div>
                <div style={statContent}>
                  <h3 style={statNumber}>{stats.activeRentals}</h3>
                  <p style={statLabel}>Active Rentals</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div style={quickActions}>
              <h3 style={sectionTitle}>Quick Actions</h3>
              <div style={actionGrid}>
                <button 
                  onClick={() => navigate('/add')} 
                  style={actionButton}
                >
                  ➕ Add New Car
                </button>
                <button 
                  onClick={() => navigate('/rent')} 
                  style={actionButton}
                >
                  📄 Create Rental
                </button>
                <button 
                  onClick={() => setActiveTab('agreements')} 
                  style={actionButton}
                >
                  📋 View Agreements
                </button>
                <button 
                  onClick={() => setActiveTab('cars')} 
                  style={actionButton}
                >
                  🚗 Manage Cars
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cars Management Tab */}
        {activeTab === 'cars' && (
          <div style={tabContent}>
            <h2 style={sectionTitle}>Car Management</h2>
            <CarEditor />
          </div>
        )}

        {/* Agreements Tab */}
        {activeTab === 'agreements' && (
          <div style={tabContent}>
            <h2 style={sectionTitle}>Rental Agreements</h2>
            <RentalAgreements />
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div style={tabContent}>
            <h2 style={sectionTitle}>⚙️ System Settings</h2>
            
            <div style={settingsCard}>
              <h3 style={subsectionTitle}>📧 Owner Email Configuration</h3>
              <p style={settingsDescription}>
                This email address will be used as the "from" address for all rental agreement emails sent to customers. 
                It will also be included as a CC recipient on all agreement notifications.
              </p>
              
              <div style={currentEmailSection}>
                <label style={emailLabel}>Current Owner Email:</label>
                <div style={emailDisplay}>
                  {ownerEmail || 'No email configured'}
                </div>
              </div>
              
              <div style={emailForm}>
                <label style={emailLabel}>Update Owner Email:</label>
                <input
                  type="email"
                  placeholder="Enter new owner email address"
                  value={newOwnerEmail}
                  onChange={(e) => setNewOwnerEmail(e.target.value)}
                  style={emailInput}
                  disabled={savingEmail}
                />
                <button 
                  onClick={handleSaveOwnerEmail} 
                  style={saveButton} 
                  disabled={savingEmail || !newOwnerEmail.trim()}
                >
                  {savingEmail ? '💾 Saving...' : '💾 Save Email'}
                </button>
              </div>
              
              <div style={infoBox}>
                <h4 style={infoTitle}>ℹ️ How this works:</h4>
                <ul style={infoList}>
                  <li>When customers complete a rental agreement, they receive an email with the PDF</li>
                  <li>This owner email will be CC'd on all agreement notifications</li>
                  <li>The email appears as the "from" address in customer communications</li>
                  <li>Changes take effect immediately for new rentals</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div style={tabContent}>
            <h2 style={sectionTitle}>Security Settings</h2>
            <div style={securityCard}>
              <h3 style={subsectionTitle}>Change Password</h3>
              <PasswordChanger />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Styles
const pageWrapper = {
  backgroundColor: '#f8fafc',
  minHeight: '100vh',
  padding: '0',
};

const loadingContainer = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  gap: '16px',
};

const spinner = {
  width: '40px',
  height: '40px',
  border: '4px solid #e5e7eb',
  borderTop: '4px solid #3b82f6',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
};

const header = {
  backgroundColor: '#fff',
  padding: '24px 32px',
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const headerLeft = {
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
};

const headerRight = {
  display: 'flex',
  alignItems: 'center',
};

const title = {
  fontSize: '28px',
  fontWeight: 700,
  color: '#111827',
  margin: 0,
};

const backButton = {
  padding: '8px 16px',
  backgroundColor: '#f3f4f6',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 500,
  fontSize: '14px',
  color: '#374151',
};

const userInfo = {
  fontSize: '14px',
  color: '#6b7280',
  fontWeight: 500,
};

const tabContainer = {
  backgroundColor: '#fff',
  padding: '0 32px',
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  gap: '8px',
};

const tabButton = {
  padding: '16px 24px',
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontWeight: 500,
  fontSize: '14px',
  color: '#6b7280',
  borderBottom: '2px solid transparent',
  transition: 'all 0.2s',
};

const activeTabButton = {
  color: '#3b82f6',
  borderBottomColor: '#3b82f6',
  backgroundColor: '#eff6ff',
};

const contentArea = {
  padding: '32px',
  maxWidth: '1200px',
  margin: '0 auto',
};

const overviewContainer = {
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
};

const sectionTitle = {
  fontSize: '24px',
  fontWeight: 600,
  color: '#111827',
  marginBottom: '24px',
};

const statsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '24px',
  marginBottom: '32px',
};

const statCard = {
  backgroundColor: '#fff',
  padding: '24px',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const statIcon = {
  fontSize: '32px',
  width: '48px',
  height: '48px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#f3f4f6',
  borderRadius: '12px',
};

const statContent = {
  flex: 1,
};

const statNumber = {
  fontSize: '28px',
  fontWeight: 700,
  color: '#111827',
  margin: '0 0 4px 0',
};

const statLabel = {
  fontSize: '14px',
  color: '#6b7280',
  margin: 0,
};

const quickActions = {
  backgroundColor: '#fff',
  padding: '24px',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
};

const actionGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '16px',
};

const actionButton = {
  padding: '16px 24px',
  backgroundColor: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '14px',
  transition: 'background-color 0.2s',
};

const tabContent = {
  backgroundColor: '#fff',
  padding: '32px',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const settingsCard = {
  backgroundColor: '#f9fafb',
  padding: '24px',
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
};

const securityCard = {
  backgroundColor: '#f9fafb',
  padding: '24px',
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
};

const subsectionTitle = {
  fontSize: '18px',
  fontWeight: 600,
  color: '#111827',
  marginBottom: '16px',
};

const settingsDescription = {
  fontSize: '14px',
  color: '#4b5563',
  marginBottom: '24px',
  lineHeight: '1.6',
};

const currentEmailSection = {
  marginBottom: '24px',
};

const emailLabel = {
  fontSize: '14px',
  color: '#4b5563',
  marginBottom: '8px',
  display: 'block',
};

const emailDisplay = {
  fontSize: '16px',
  color: '#374151',
  fontWeight: 500,
  padding: '12px 16px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  backgroundColor: '#f3f4f6',
};

const emailForm = {
  marginBottom: '24px',
};

const emailInput = {
  padding: '12px 16px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontSize: '14px',
  width: '100%',
  marginBottom: '16px',
};

const saveButton = {
  padding: '12px 24px',
  backgroundColor: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '14px',
  transition: 'background-color 0.2s',
};

const infoBox = {
  backgroundColor: '#e0f2fe',
  padding: '24px',
  borderRadius: '8px',
  border: '1px solid #cce5ff',
  marginTop: '32px',
};

const infoTitle = {
  fontSize: '16px',
  fontWeight: 600,
  color: '#007bff',
  marginBottom: '16px',
};

const infoList = {
  listStyle: 'disc',
  paddingLeft: '20px',
  color: '#343a40',
};
